import { Fragment, useEffect, useMemo, useState } from 'react';
import { useResponsive } from '@/hooks';
import {
  MenuItem,
  MenuLink,
  MenuTitle,
  MenuArrow,
  Menu,
  KeenIcon
} from '@/components';
import {
  MegaMenuSubBilling,
  MegaMenuSubDocuments,
  MegaMenuSubReports,
  MegaMenuSubContact,
  MegaMenuSubMatter,
  MegaMenuBasicSetup,
  MegaMenuSubAttorney,
  MegaMenuMassMailing
} from '@/partials/menu/mega-menu';
import { MENU_MEGA } from '@/config';
import { useLanguage } from '@/i18n';
import { useAbility } from '@/providers/';
import { useAuthContext } from '@/auth';
import { Skeleton } from '@/components/ui/skeleton'

const MegaMenuInnerReferral = () => {
  const { ability } = useAbility();
  const { auth, loading } = useAuthContext();
  const user = auth?.user;

  const desktopMode = useResponsive('up', 'lg');
  const { isRTL } = useLanguage();

  const linkClass =
    'lg:py-3.5 border-b border-b-transparent menu-item-active:border-b-gray-800 text-gray-800 menu-item-hover:text-gray-900 menu-item-active:text-gray-900 menu-item-here:border-b-gray-800 menu-item-here:text-gray-900';
  const titleClass = 'font-medium text-gray-800 text-sm';

  const isSuperAdmin = user?.roles?.includes('super-admin');
  const isAdmin = user?.permissions?.includes('is_admin_access');
  const [menuReady, setMenuReady] = useState(false);

  useEffect(() => {
    if (user && !loading && ability) {
      setMenuReady(true);
    }
  }, [user, loading, ability]);

  const filterMenuChildren = (item) => {
    if (isSuperAdmin || isAdmin) {
      return item.children || [];
    }

    const filterRecursive = (children = []) =>
      children
        .map((child) => {
          if (child.children && child.children.length > 0) {
            const filteredChildren = filterRecursive(child.children);
            if (
              !child.permission ||
              ability.can(child.permission.action, child.permission.subject) ||
              filteredChildren.length > 0
            ) {
              return {
                ...child,
                children: filteredChildren
              };
            }
            return null;
          }

          if (!child.permission) return child;
          if (ability.can(child.permission.action, child.permission.subject)) {
            return child;
          }

          return null;
        })
        .filter(Boolean);

    return filterRecursive(item.children || []);
  };

  const filteredMenuItems = useMemo(() => {
    if (!menuReady) return []

    // const items = structuredClone(MENU_MEGA);
    const items = MENU_MEGA.map(section => ({
      ...section,
      children: section.children?.map(child => ({ ...child })) ?? [],
    }));

    // items[1].children = filterMenuChildren(items[1]); // Contacts
    // items[2].children = filterMenuChildren(items[2]); // Matters
    // items[3].children = filterMenuChildren(items[3]); // Billing
    // items[4].children = filterMenuChildren(items[4]); // Documents
    // items[5].children = filterMenuChildren(items[5]); // Reports
    // items[6].children = filterMenuChildren(items[6]); // Basic Setup
    items[7].children = filterMenuChildren(items[7]); // Referral
    // items[8].children = filterMenuChildren(items[8]); // Mass Mailing

    return items;
  }, [menuReady, user, ability]);

  if (!menuReady) {
    return (
      <div className='flex items-center py-3.5 gap-2.5'>
        {[...Array(6)].map((_, idx) => (
          <p key={idx} className='h-[20px]'>
            <Skeleton className="w-[100px] h-[16px]" />
          </p>
        ))}
      </div>
    );
  }

  const buildArrow = () => (
    <MenuArrow className="flex text-gray-400 lg:hidden">
      <KeenIcon icon="plus" className="text-2xs menu-item-show:hidden" />
      <KeenIcon icon="minus" className="hidden text-2xs menu-item-show:inline-flex" />
    </MenuArrow>
  );

  const renderItems = () => {
    const referralItem = filteredMenuItems?.[7] ?? { children: [] };;

    return (
      <Fragment>
        {/* <MenuItem key="home">
          <MenuLink path={homeItem.path} className={linkClass}>
            <MenuTitle className={titleClass}>{homeItem.title}</MenuTitle>
          </MenuLink>
        </MenuItem> */}

        {referralItem.children.length > 0 && (
          <MenuItem key="referrals" toggle={desktopMode ? 'dropdown' : 'accordion'} trigger={desktopMode ? 'hover' : 'click'} dropdownProps={{
            placement: isRTL() ? 'bottom-end' : 'bottom-start',
            modifiers: [{ name: 'offset', options: { offset: isRTL() ? [20, 0] : [-20, 0] } }]
          }}>
            <MenuLink className={linkClass}>
              <MenuTitle className={titleClass}>{referralItem.title}</MenuTitle>
              {buildArrow()}
            </MenuLink>
            {MegaMenuSubAttorney(filteredMenuItems)}
          </MenuItem>
        )}
      </Fragment>
    );
  };

  return (
    <Menu multipleExpand highlight className="flex-col lg:flex-row gap-5 lg:gap-7.5 p-5 lg:p-0">
      {renderItems()}
    </Menu>
  );
};

export { MegaMenuInnerReferral };
